import { useCallback, useRef, useState } from 'react';

import { useStorageToken } from '~lib/auth/use-storage-token';
import {
  downloadDatasetToFolder,
  type DownloadDatasetOptions,
} from '~lib/storage/download-dataset';
import type { Dataset } from '~lib/storage/datasets';

interface UseDownloadDatasetResult {
  downloadingName: string | null;
  bytesDownloaded: number;
  error: Error | null;
  download: (dataset: Dataset) => Promise<void>;
  cancel: () => void;
}

// Tracks at most one in-flight dataset download. If the user clicks another
// dataset's button mid-download, the new request is rejected — keeps the
// progress accounting honest and avoids fighting the directory picker.
export function useDownloadDataset(): UseDownloadDatasetResult {
  const getToken = useStorageToken();
  const [downloadingName, setDownloadingName] = useState<string | null>(null);
  const [bytesDownloaded, setBytesDownloaded] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const download = useCallback(
    async (dataset: Dataset) => {
      if (abortRef.current) return;
      setError(null);
      setBytesDownloaded(0);
      setDownloadingName(dataset.name);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await getToken();
        const options: DownloadDatasetOptions = {
          signal: controller.signal,
          onProgress: setBytesDownloaded,
        };
        await downloadDatasetToFolder(token, dataset, options);
      } catch (e) {
        // The directory picker throws AbortError when the user cancels the
        // OS dialog; that's not really an error worth surfacing.
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setDownloadingName(null);
        abortRef.current = null;
      }
    },
    [getToken],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { downloadingName, bytesDownloaded, error, download, cancel };
}
